import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Alert, Badge, Button, Card, Checkbox, Field, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { can } from '../../lib/permissions';
import { PeopleNav } from '../people/PeopleNav';
import type { SupplierListItem } from './data';
import { SupplierForm } from './SupplierForm';

/** Proveedores (guía 7.7): los mantiene IT; Finanzas y Contabilidad los consultan para registrarlos en Sage. */
export function SuppliersPage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [inactive, setInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const query = new URLSearchParams();
  if (debounced) query.set('buscar', debounced);
  if (inactive) query.set('inactivos', 'true');
  const list = useQuery({
    queryKey: ['proveedores', 'lista', debounced, inactive],
    queryFn: () => api<SupplierListItem[]>(`/api/proveedores?${query}`),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-tinta">{t('pv.titulo')}</h1>
          <p className="max-w-2xl text-sm text-slate-600">{t('pv.descripcion')}</p>
        </div>
        {can(me, 'proveedores.gestionar') && !creating && <Button onClick={() => setCreating(true)}>{t('pv.nuevo')}</Button>}
      </div>
      <PeopleNav />

      {creating && (
        <Card title={t('pv.nuevo')}>
          <SupplierForm
            submitLabel={t('pv.crear')}
            onCancel={() => setCreating(false)}
            onSubmit={async (values) => {
              const created = await api<{ id: string }>('/api/proveedores', { json: values });
              await qc.invalidateQueries({ queryKey: ['proveedores'] });
              void navigate(`/proveedores/${created.id}`, { state: { created: true } });
            }}
          />
        </Card>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <div className="w-72">
          <Field id="pv-buscar" type="search" label={t('us.buscar')} placeholder={t('pv.buscar_ayuda')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="pb-2">
          <Checkbox checked={inactive} onChange={(e) => setInactive(e.target.checked)} label={t('pv.ver_inactivos')} />
        </div>
      </div>

      <Card className="p-0">
        {!list.data ? (
          <div className="p-5">{list.error ? <Alert>{errorMessage(list.error)}</Alert> : <Spinner label={t('comun.cargando')} />}</div>
        ) : list.data.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">{t('pv.vacio')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">{t('pv.col_proveedor')}</th>
                  <th className="px-4 py-2 font-medium">{t('pv.campo_taxId')}</th>
                  <th className="px-4 py-2 font-medium">{t('pv.contacto')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.data.map((s) => (
                  <tr key={s.id} className={`hover:bg-slate-50 ${s.active ? '' : 'text-slate-400'}`}>
                    <td className="px-4 py-2">
                      <Link to={`/proveedores/${s.id}`} className="font-medium text-slate-900 hover:underline">
                        {s.tradeName}
                      </Link>
                      {!s.active && (
                        <span className="ml-1.5">
                          <Badge>{t('pv.inactivo')}</Badge>
                        </span>
                      )}
                      <span className="block text-xs text-slate-500">
                        {s.legalName}
                        {s.country && ` · ${s.country}`}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{s.taxId}</td>
                    <td className="px-4 py-2 text-slate-700">
                      {s.email ?? '—'}
                      {s.phone && <span className="block text-xs text-slate-500">{s.phone}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
